/**
 * SPA entry: QueryClient (402 → upsell bridge), i18n, auth, then the router. Everything that
 * needs router context lives in the root route (`app/providers.tsx`).
 */
import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createQueryClient, handleUnauthorized, setupApi } from './app/api';
import './app/app.css';
import { AuthProvider } from './app/auth';
import { I18nProvider } from './app/i18n';
import { runtime } from './app/runtime';
import { routeTree } from './routeTree.gen';

const queryClient = createQueryClient({
  onEntitlementMissing: (problem) => runtime.openUpsell?.(problem),
});

setupApi({
  getOrgId: () => runtime.orgId,
  onUnauthorized: handleUnauthorized,
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <AuthProvider onChange={() => queryClient.clear()}>
          <RouterProvider router={router} />
        </AuthProvider>
      </I18nProvider>
    </QueryClientProvider>
  </StrictMode>,
);
